import React from 'react';

const API_URL = 'https://heybiseo.ai.kr';
const WEEKDAYS = ['월', '화', '수', '목', '금', '토', '일'];

const card = {
  background: '#fff', borderRadius: 16, padding: 24,
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #eef2f7',
};
const sectionTitle = { fontSize: 17, fontWeight: 800, color: '#111827', marginBottom: 4 };
const sectionDesc = { fontSize: 13, color: '#6b7280', marginBottom: 18 };

function AnalyticsPage({ token, addToast }) {
  const [retention, setRetention] = React.useState(null);
  const [heatmap, setHeatmap] = React.useState(null);
  const [voucher, setVoucher] = React.useState(null);
  const [finance, setFinance] = React.useState(null);
  const [leadConv, setLeadConv] = React.useState(null);
  const [acquisition, setAcquisition] = React.useState(null);
  const [dormant, setDormant] = React.useState(null);
  const [heatDays, setHeatDays] = React.useState(90);
  const [unit, setUnit] = React.useState('month');  // month | year
  const [loading, setLoading] = React.useState(true);
  const [errMsg, setErrMsg] = React.useState('');

  const headers = React.useMemo(
    () => ({ 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }),
    [token]
  );

  const loadAll = React.useCallback(async (days, u) => {
    setLoading(true);
    setErrMsg('');
    const getJson = async (path) => {
      const res = await fetch(`${API_URL}${path}`, { headers });
      if (!res.ok) throw new Error(`${path} → ${res.status}`);
      return res.json();
    };
    try {
      const [r, h, v, f, l, a, d] = await Promise.all([
        getJson(`/analytics/retention?months=6`),
        getJson(`/analytics/visit-heatmap?days=${days}`),
        getJson(`/analytics/voucher-usage`),
        getJson(`/analytics/finance?unit=${u}`),
        getJson(`/analytics/lead-conversion?unit=${u}`),
        getJson(`/analytics/acquisition?unit=${u}`),
        getJson(`/analytics/dormant`),
      ]);
      setRetention(r); setHeatmap(h); setVoucher(v);
      setFinance(f); setLeadConv(l); setAcquisition(a); setDormant(d);
    } catch (e) {
      setErrMsg(`통계를 불러오지 못했어요 (${e.message || '네트워크 오류'})`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers]);

  React.useEffect(() => { loadAll(heatDays, unit); }, [loadAll, heatDays, unit]);

  return (
    <div style={{ padding: '4px 0', width: '100%' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111', margin: 0 }}>통계분석</h1>
          <p style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>
            센터 운영을 한눈에 보는 핵심 지표를 모았어요. 매출·신규·전환은 기간 단위로 분석할 수 있어요.
          </p>
        </div>
        <div style={{ display: 'flex', background: '#eef2f7', borderRadius: 10, padding: 4 }}>
          {[['month', '월별'], ['year', '연별']].map(([u, lbl]) => (
            <button key={u} onClick={() => setUnit(u)}
              style={{
                padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 700,
                background: unit === u ? '#fff' : 'transparent',
                color: unit === u ? '#2563eb' : '#6b7280',
                boxShadow: unit === u ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}>{lbl}</button>
          ))}
        </div>
      </div>

      {loading && <div style={{ ...card, textAlign: 'center', color: '#9ca3af' }}>불러오는 중…</div>}

      {!loading && errMsg && (
        <div style={{ ...card, textAlign: 'center', color: '#ef4444', fontSize: 14 }}>
          {errMsg}
          <div style={{ marginTop: 12 }}>
            <button onClick={() => loadAll(heatDays)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 13 }}>다시 시도</button>
          </div>
        </div>
      )}

      {!loading && !errMsg && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* ── 4. 매출·신규회원 추이 ── */}
          <FinanceSection data={finance} unit={unit} />
          {/* ── 1. 재등록률 ── */}
          <RetentionSection data={retention} />
          {/* ── 5. 상담 전환율 ── */}
          <LeadConversionSection data={leadConv} unit={unit} />
          {/* ── 6. 신규회원 유입경로 ── */}
          <AcquisitionSection data={acquisition} unit={unit} />
          {/* ── 2. 방문 히트맵 ── */}
          <HeatmapSection data={heatmap} days={heatDays} setDays={setHeatDays} />
          {/* ── 3. 이용권 소진률 ── */}
          <VoucherSection data={voucher} />
          {/* ── 7. 이탈·휴면 회원 ── */}
          <DormantSection data={dormant} />
        </div>
      )}
    </div>
  );
}

// ── 1. 재등록률(리텐션) ──────────────────────────────────────────────
function RetentionSection({ data }) {
  if (!data) return null;
  const series = data.series || [];
  const maxRate = Math.max(100, ...series.map(s => s.rate));
  return (
    <div style={card}>
      <div style={sectionTitle}>1. 재등록률 (리텐션)</div>
      <div style={sectionDesc}>이용권이 만료된 회원 중 재등록(연장)한 비율이에요. 센터 건강도의 핵심 지표예요.</div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <Kpi label="최근 6개월 평균 재등록률" value={`${data.overall_rate}%`} accent="#2563eb" big />
        <Kpi label="만료 회원(누적)" value={`${data.total_expired}명`} />
        <Kpi label="재등록 회원(누적)" value={`${data.total_reregistered}명`} accent="#16a34a" />
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: 180, paddingTop: 8 }}>
        {series.map((s, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#2563eb' }}>{s.rate}%</div>
            <div style={{ width: '100%', maxWidth: 48, height: 120, background: '#f1f5f9', borderRadius: 8, display: 'flex', alignItems: 'flex-end', overflow: 'hidden' }}>
              <div style={{ width: '100%', height: `${(s.rate / maxRate) * 100}%`, background: 'linear-gradient(180deg,#3b82f6,#2563eb)', borderRadius: 8, minHeight: s.rate > 0 ? 4 : 0 }} />
            </div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>{s.month.slice(5)}월</div>
            <div style={{ fontSize: 10, color: '#9ca3af' }}>{s.reregistered}/{s.expired}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 2. 요일·시간대 방문 히트맵 ───────────────────────────────────────
function HeatmapSection({ data, days, setDays }) {
  if (!data) return null;
  const matrix = data.matrix || [];
  const hours = Array.from({ length: 24 }, (_, h) => h);
  let max = 0;
  matrix.forEach(row => row.forEach(c => { if (c > max) max = c; }));
  const color = (c) => {
    if (!c) return '#f8fafc';
    const a = 0.12 + 0.88 * (c / max);
    return `rgba(37,99,235,${a.toFixed(2)})`;
  };
  const peak = data.peak || {};
  const peakTxt = peak.count > 0
    ? `${WEEKDAYS[peak.weekday]}요일 ${String(peak.hour).padStart(2, '0')}시 (${peak.count}회)`
    : '데이터 없음';

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={sectionTitle}>2. 요일·시간대 방문 혼잡도</div>
          <div style={sectionDesc}>언제 회원이 가장 많이 오는지 보여줘요. 인력 배치·청소·수업 편성에 활용하세요.</div>
        </div>
        <select value={days} onChange={e => setDays(Number(e.target.value))}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, color: '#374151' }}>
          <option value={30}>최근 30일</option>
          <option value={90}>최근 90일</option>
          <option value={180}>최근 180일</option>
        </select>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <Kpi label="가장 붐비는 시간" value={peakTxt} accent="#2563eb" />
        <Kpi label="총 방문" value={`${data.total}회`} />
      </div>

      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 720 }}>
          <div style={{ display: 'flex', paddingLeft: 28 }}>
            {hours.map(h => (
              <div key={h} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: '#9ca3af' }}>
                {h % 3 === 0 ? h : ''}
              </div>
            ))}
          </div>
          {matrix.map((row, wd) => (
            <div key={wd} style={{ display: 'flex', alignItems: 'center', marginTop: 3 }}>
              <div style={{ width: 28, fontSize: 11, fontWeight: 700, color: '#6b7280' }}>{WEEKDAYS[wd]}</div>
              {row.map((c, h) => (
                <div key={h} title={`${WEEKDAYS[wd]} ${h}시: ${c}회`}
                  style={{ flex: 1, aspectRatio: '1 / 1', maxHeight: 22, margin: 1, borderRadius: 3, background: color(c) }} />
              ))}
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, fontSize: 11, color: '#9ca3af' }}>
            <span>적음</span>
            {[0.12, 0.35, 0.6, 0.85, 1].map((a, i) => (
              <div key={i} style={{ width: 18, height: 12, borderRadius: 3, background: `rgba(37,99,235,${a})` }} />
            ))}
            <span>많음</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 3. 이용권 소진률 ─────────────────────────────────────────────────
function VoucherSection({ data }) {
  if (!data) return null;
  const buckets = data.buckets || {};
  const bucketDefs = [
    { key: '0-25', label: '0~25%', color: '#fca5a5' },
    { key: '25-50', label: '25~50%', color: '#fdba74' },
    { key: '50-75', label: '50~75%', color: '#93c5fd' },
    { key: '75-100', label: '75~100%', color: '#86efac' },
  ];
  const maxB = Math.max(1, ...bucketDefs.map(b => buckets[b.key] || 0));

  return (
    <div style={card}>
      <div style={sectionTitle}>3. 이용권 소진률</div>
      <div style={sectionDesc}>횟수제 이용권(수강권)이 얼마나 사용되고 있는지예요. 소진이 빠를수록 재등록 가능성이 높아요.</div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <Kpi label="평균 소진률" value={`${data.avg_rate}%`} accent="#2563eb" big />
        <Kpi label="평균 잔여 횟수" value={`${data.avg_remaining}회`} />
        <Kpi label="유효 횟수제 이용권" value={`${data.count}건`} />
      </div>

      <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10 }}>소진률 구간별 분포</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, height: 130, marginBottom: 24 }}>
        {bucketDefs.map(b => {
          const v = buckets[b.key] || 0;
          return (
            <div key={b.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{v}건</div>
              <div style={{ width: '100%', maxWidth: 64, height: 80, background: '#f1f5f9', borderRadius: 8, display: 'flex', alignItems: 'flex-end', overflow: 'hidden' }}>
                <div style={{ width: '100%', height: `${(v / maxB) * 100}%`, background: b.color, borderRadius: 8, minHeight: v > 0 ? 4 : 0 }} />
              </div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>{b.label}</div>
            </div>
          );
        })}
      </div>

      {data.plans && data.plans.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10 }}>이용권별 평균 소진률</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.plans.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 140, fontSize: 13, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                <div style={{ flex: 1, height: 16, background: '#f1f5f9', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ width: `${p.avg_rate}%`, height: '100%', background: 'linear-gradient(90deg,#60a5fa,#2563eb)', borderRadius: 8 }} />
                </div>
                <div style={{ width: 70, fontSize: 12, fontWeight: 700, color: '#2563eb', textAlign: 'right' }}>{p.avg_rate}%</div>
                <div style={{ width: 40, fontSize: 11, color: '#9ca3af', textAlign: 'right' }}>{p.count}건</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── 4. 매출·신규회원 추이 + 매출 구성 ────────────────────────────────
const won = (n) => `${(n || 0).toLocaleString()}원`;
const BAR_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899', '#64748b'];
const fmtLabel = (l, unit) => unit === 'year' ? `${l}년` : `${l.slice(5)}월`;

function FinanceSection({ data, unit }) {
  const [sel, setSel] = React.useState(null);
  if (!data) return null;
  const series = data.series || [];
  const maxRev = Math.max(1, ...series.map(s => s.revenue));
  const maxNew = Math.max(1, ...series.map(s => s.new_members));
  const cur = sel != null ? series.find(s => s.label === sel) : series[series.length - 1];

  const breakdown = (obj) => {
    const entries = Object.entries(obj || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
    const tot = entries.reduce((s, [, v]) => s + v, 0) || 1;
    return entries.map(([k, v], i) => ({ k, v, pct: Math.round(v / tot * 100), color: BAR_COLORS[i % BAR_COLORS.length] }));
  };
  const cats = cur ? breakdown(cur.by_category) : [];
  const meths = cur ? breakdown(cur.by_method) : [];

  return (
    <div style={card}>
      <div style={sectionTitle}>4. 매출 · 신규회원 추이</div>
      <div style={sectionDesc}>{unit === 'year' ? '최근 5년' : '최근 12개월'} 매출과 신규 등록 흐름이에요. 상단 월별/연별로 단위를 바꿀 수 있어요.</div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <Kpi label={`${unit === 'year' ? '기간' : '연간'} 총매출`} value={won(data.total_revenue)} accent="#2563eb" big />
        <Kpi label="총 신규회원" value={`${data.total_new_members}명`} accent="#16a34a" />
        <Kpi label="총 환불" value={won(data.total_refund)} accent="#ef4444" />
      </div>

      {/* 매출 막대 + 신규회원 점 */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 200, marginBottom: 6 }}>
        {series.map((s, i) => {
          const active = cur && s.label === cur.label;
          return (
            <div key={i} onClick={() => setSel(s.label)}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <div style={{ fontSize: 10, color: '#16a34a', fontWeight: 700 }}>{s.new_members > 0 ? `+${s.new_members}` : ''}</div>
              <div style={{ width: '100%', maxWidth: 44, height: 140, background: '#f1f5f9', borderRadius: 6, display: 'flex', alignItems: 'flex-end', overflow: 'hidden', outline: active ? '2px solid #2563eb' : 'none' }}>
                <div style={{ width: '100%', height: `${(s.revenue / maxRev) * 100}%`, background: active ? 'linear-gradient(180deg,#3b82f6,#1d4ed8)' : 'linear-gradient(180deg,#93c5fd,#3b82f6)', borderRadius: 6, minHeight: s.revenue > 0 ? 3 : 0 }} />
              </div>
              <div style={{ fontSize: 10, color: active ? '#2563eb' : '#9ca3af', fontWeight: active ? 700 : 400 }}>{fmtLabel(s.label, unit)}</div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 20 }}>막대=매출 · 초록숫자=신규회원 · 막대를 클릭하면 아래 구성이 바뀌어요</div>

      {/* 선택 기간 매출 구성 */}
      {cur && (
        <div style={{ background: '#f8fafc', borderRadius: 12, padding: 18, border: '1px solid #eef2f7' }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#374151', marginBottom: 14 }}>
            {fmtLabel(cur.label, unit)} 매출 구성 · {won(cur.revenue)}
          </div>
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
            <CompositionList title="카테고리별" items={cats} />
            <CompositionList title="결제수단별" items={meths} />
          </div>
        </div>
      )}
    </div>
  );
}

function CompositionList({ title, items }) {
  return (
    <div style={{ flex: '1 1 240px', minWidth: 240 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 10 }}>{title}</div>
      {items.length === 0 && <div style={{ fontSize: 12, color: '#9ca3af' }}>데이터 없음</div>}
      {items.map((it, i) => (
        <div key={i} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
            <span style={{ color: '#374151' }}>{it.k}</span>
            <span style={{ color: '#6b7280' }}>{won(it.v)} ({it.pct}%)</span>
          </div>
          <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${it.pct}%`, height: '100%', background: it.color, borderRadius: 4 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── 5. 상담 전환율 ───────────────────────────────────────────────────
function LeadConversionSection({ data, unit }) {
  if (!data) return null;
  const series = data.series || [];
  const maxRate = Math.max(100, ...series.map(s => s.rate));
  const f = data.funnel || {};
  const funnelDefs = [
    { k: 'NEW', label: '신규 접수', color: '#93c5fd' },
    { k: 'CONSULTING', label: '상담 중', color: '#60a5fa' },
    { k: 'CONVERTED', label: '회원 전환', color: '#16a34a' },
    { k: 'CANCELLED', label: '취소/포기', color: '#d1d5db' },
  ];
  const maxF = Math.max(1, ...funnelDefs.map(d => f[d.k] || 0));
  return (
    <div style={card}>
      <div style={sectionTitle}>5. 상담 → 등록 전환율</div>
      <div style={sectionDesc}>잠재고객(상담) 중 실제 회원으로 전환된 비율이에요. 마케팅·영업 성과 지표예요.</div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <Kpi label="전체 전환율" value={`${data.overall_rate}%`} accent="#2563eb" big />
        <Kpi label="총 상담" value={`${data.total_leads}건`} />
        <Kpi label="전환 회원" value={`${data.total_converted}명`} accent="#16a34a" />
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 160, marginBottom: 24 }}>
        {series.map((s, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#2563eb' }}>{s.rate}%</div>
            <div style={{ width: '100%', maxWidth: 40, height: 110, background: '#f1f5f9', borderRadius: 6, display: 'flex', alignItems: 'flex-end', overflow: 'hidden' }}>
              <div style={{ width: '100%', height: `${(s.rate / maxRate) * 100}%`, background: 'linear-gradient(180deg,#34d399,#16a34a)', borderRadius: 6, minHeight: s.rate > 0 ? 3 : 0 }} />
            </div>
            <div style={{ fontSize: 10, color: '#9ca3af' }}>{fmtLabel(s.label, unit)}</div>
            <div style={{ fontSize: 9, color: '#cbd5e1' }}>{s.converted}/{s.leads}</div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10 }}>상담 단계 현황 (전체)</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {funnelDefs.map(d => (
          <div key={d.k} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 80, fontSize: 12, color: '#374151' }}>{d.label}</div>
            <div style={{ flex: 1, height: 18, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ width: `${((f[d.k] || 0) / maxF) * 100}%`, height: '100%', background: d.color, borderRadius: 6 }} />
            </div>
            <div style={{ width: 50, fontSize: 12, fontWeight: 700, color: '#374151', textAlign: 'right' }}>{f[d.k] || 0}건</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 6. 신규회원 유입경로 ─────────────────────────────────────────────
function AcquisitionSection({ data, unit }) {
  if (!data) return null;
  const routes = data.routes || [];
  const max = Math.max(1, ...routes.map(r => r.count));
  return (
    <div style={card}>
      <div style={sectionTitle}>6. 신규회원 유입경로</div>
      <div style={sectionDesc}>{unit === 'year' ? '최근 5년' : '최근 1년'} 신규 회원이 어떤 경로로 등록했는지예요. 지역별 마케팅 채널 판단에 활용하세요.</div>

      {routes.length === 0 && <div style={{ fontSize: 13, color: '#9ca3af' }}>데이터가 없어요.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {routes.map((r, i) => {
          const pct = Math.round(r.count / data.total * 100);
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 110, fontSize: 13, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.route}</div>
              <div style={{ flex: 1, height: 18, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ width: `${(r.count / max) * 100}%`, height: '100%', background: BAR_COLORS[i % BAR_COLORS.length], borderRadius: 6 }} />
              </div>
              <div style={{ width: 80, fontSize: 12, fontWeight: 700, color: '#374151', textAlign: 'right' }}>{r.count}명 ({pct}%)</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── 7. 이탈·휴면 회원 ────────────────────────────────────────────────
function DormantSection({ data }) {
  if (!data) return null;
  const b = data.buckets || {};
  const defs = [
    { k: '30-60', label: '30~60일', color: '#fcd34d' },
    { k: '60-90', label: '60~90일', color: '#fb923c' },
    { k: '90+', label: '90일+', color: '#ef4444' },
    { k: 'never', label: '방문기록 없음', color: '#9ca3af' },
  ];
  const list = data.list || [];
  return (
    <div style={card}>
      <div style={sectionTitle}>7. 이탈 · 휴면 회원</div>
      <div style={sectionDesc}>유효 회원 중 한동안 방문하지 않은 회원이에요. 재유치(리마인드 문자·전화) 대상으로 활용하세요.</div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <Kpi label="유효 회원" value={`${data.total_active}명`} />
        <Kpi label="휴면 의심(30일+)" value={`${data.dormant_total}명`} accent="#ef4444" big />
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 22, flexWrap: 'wrap' }}>
        {defs.map(d => (
          <div key={d.k} style={{ flex: '1 1 120px', minWidth: 120, background: '#f8fafc', borderRadius: 10, padding: '12px 14px', borderLeft: `4px solid ${d.color}` }}>
            <div style={{ fontSize: 12, color: '#6b7280' }}>{d.label} 미방문</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#374151' }}>{b[d.k] || 0}명</div>
          </div>
        ))}
      </div>

      {list.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10 }}>재유치 우선 대상 (오래된 순)</div>
          <div style={{ border: '1px solid #eef2f7', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ display: 'flex', background: '#f8fafc', fontSize: 12, fontWeight: 700, color: '#6b7280', padding: '10px 14px' }}>
              <div style={{ flex: 2 }}>이름</div><div style={{ flex: 2 }}>연락처</div><div style={{ flex: 2 }}>최근 방문</div><div style={{ flex: 1, textAlign: 'right' }}>경과</div>
            </div>
            {list.map((m, i) => (
              <div key={i} style={{ display: 'flex', fontSize: 13, color: '#374151', padding: '10px 14px', borderTop: '1px solid #f1f5f9' }}>
                <div style={{ flex: 2 }}>{m.name}</div>
                <div style={{ flex: 2, color: '#6b7280' }}>{m.phone || '-'}</div>
                <div style={{ flex: 2, color: '#6b7280' }}>{m.last_visit || '기록 없음'}</div>
                <div style={{ flex: 1, textAlign: 'right', color: m.days_ago >= 90 ? '#ef4444' : '#f59e0b', fontWeight: 700 }}>{m.days_ago != null ? `${m.days_ago}일` : '-'}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, accent = '#374151', big }) {
  return (
    <div style={{ flex: '1 1 150px', minWidth: 150, background: '#f8fafc', borderRadius: 12, padding: '16px 18px', border: '1px solid #eef2f7' }}>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: big ? 28 : 18, fontWeight: 900, color: accent }}>{value}</div>
    </div>
  );
}

export default AnalyticsPage;
